import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Home from './pages/Home.jsx';
import About from './pages/About.jsx';
import Contact from './pages/Contact.jsx';
import LogicGames from './pages/LogicGames.jsx';
import WordGames from './pages/WordGames.jsx';
import CardGames from './pages/CardGames.jsx';
import Apps from './pages/Apps.jsx';
import CookieConsent from './components/CookieConsent.jsx';
import Footer from './components/Footer.jsx';
import NotFound from './pages/NotFound';
import GoPage from './pages/GoPage.jsx';
import GamePage from './pages/games/GamePage.jsx';
import CrownsPage from './pages/games/CrownsPage.jsx';
import BinarySudokuPlusPage from './pages/games/BinarySudokuPlusPage.jsx';
import AquariumPage from './pages/games/AquariumPage.jsx';
import DotStreamPage from './pages/games/DotStreamPage.jsx';
import PalettePathPage from './pages/games/PalettePathPage.jsx';
import DailyLogicPuzzlesPage from './pages/games/DailyLogicPuzzlesPage.jsx';
import SolitaireProCollectionPage from './pages/games/SolitaireProCollectionPage.jsx';
import FoxHedgePage from './pages/games/FoxHedgePage.jsx';
import NonogramsPage from './pages/games/NonogramsPage.jsx';
import CalcudokuPage from './pages/games/CalcudokuPage.jsx';
import CrownsAppPage from './pages/apps/CrownsAppPage.jsx';
import BinarySudokuAppPage from './pages/apps/BinarySudokuAppPage.jsx';
import AquariumAppPage from './pages/apps/AquariumAppPage.jsx';
import PalettePathAppPage from './pages/apps/PalettePathAppPage.jsx';
import DotStreamAppPage from './pages/apps/DotStreamAppPage.jsx';
import DailyLogicPuzzlesAppPage from './pages/apps/DailyLogicPuzzlesAppPage.jsx';
import { HelmetProvider } from 'react-helmet-async';
import FAQ from './pages/FAQ.jsx';
import CanonicalUrl from './components/CanonicalUrl';
import TrailingSlashRedirect from './components/TrailingSlashRedirect';
import SmartAppBanner from './components/SmartAppBanner';
import GameContentPage from './pages/games/GameContentPage.jsx';
import gameContentData from './pages/games/gameContentData.js';
import { GENERIC_PRINTABLES } from './data/printables';
import { AccountProvider } from './account/AccountContext.jsx';

// Code-split heavy, less-visited routes so the main bundle stays lean.
// Blog pages pull in the markdown stack + all post content; printable pages pull in jsPDF.
const Blog = lazy(() => import('./pages/Blog.jsx'));
const BlogPostPage = lazy(() => import('./pages/blog/[slug].jsx'));
const Press = lazy(() => import('./pages/Press.jsx'));
const Spooktober = lazy(() => import('./pages/Spooktober.jsx'));
const EmbedDailyPage = lazy(() => import('./pages/embed/EmbedDailyPage.jsx'));
const EmbedInfoPage = lazy(() => import('./pages/embed/EmbedInfoPage.jsx'));
const TeachersLibraries = lazy(() => import('./pages/TeachersLibraries.jsx'));
const CrownsPuzzlePage = lazy(() => import('./pages/crowns-puzzle/CrownsPuzzlePage.jsx'));
const CrownsPuzzleHowToPage = lazy(() => import('./pages/crowns-puzzle/CrownsPuzzleHowToPage.jsx'));
const CrownsPuzzleHintsPage = lazy(() => import('./pages/crowns-puzzle/CrownsPuzzleHintsPage.jsx'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy.jsx'));
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService.jsx'));
const RefundPolicy = lazy(() => import('./pages/legal/RefundPolicy.jsx'));
const PrintableCrownsPage = lazy(() => import('./pages/printable/PrintableCrownsPage.jsx'));
const PrintableBinarySudokuPage = lazy(() => import('./pages/printable/PrintableBinarySudokuPage.jsx'));
const PrintableAquariumPage = lazy(() => import('./pages/printable/PrintableAquariumPage.jsx'));
const PrintableSudokuPage = lazy(() => import('./pages/printable/PrintableSudokuPage.jsx'));
const PrintableTentsPage = lazy(() => import('./pages/printable/PrintableTentsPage.jsx'));
const PrintableNonogramsPage = lazy(() => import('./pages/printable/PrintableNonogramsPage.jsx'));
const PrintableCalcudokuPage = lazy(() => import('./pages/printable/PrintableCalcudokuPage.jsx'));
const PrintablePuzzlePage = lazy(() => import('./pages/printable/PrintablePuzzlePage.jsx'));
const PrintableSetRoute = lazy(() => import('./pages/printable/PrintableSetRoute.jsx'));
const PrintableHubPage = lazy(() => import('./pages/printable/PrintableHubPage.jsx'));
const AccountPage = lazy(() => import('./pages/account/AccountPage.jsx'));
const DeleteAccountPage = lazy(() => import('./pages/account/DeleteAccountPage.jsx'));
const PlusPage = lazy(() => import('./pages/account/PlusPage.jsx'));
const WeeklyPackPage = lazy(() => import('./pages/printable/WeeklyPackPage.jsx'));

// Single authoritative Organization + WebSite graph, injected on every page.
const siteSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://logicpuzzles.ca/#organization",
      "name": "Logic Puzzles CA",
      "alternateName": "LogicPuzzles.ca",
      "url": "https://logicpuzzles.ca",
      "logo": {
        "@type": "ImageObject",
        "url": "https://logicpuzzles.ca/android-icon-192x192.png",
        "width": 192,
        "height": 192
      },
      "description": "Free online logic puzzle games including Sudoku, Nonograms, Calcudoku, Crowns, Binary Sudoku+, Aquarium, and 30+ more. Hand-crafted puzzles designed for pure logical deduction.",
      "email": "ben@sparkandspiral.ca",
      "founder": {
        "@type": "Person",
        "name": "Ben Miller",
        "url": "https://www.linkedin.com/in/benmiller12/"
      },
      "sameAs": [
        "https://www.instagram.com/sparkandspiralsoftware",
        "https://www.tiktok.com/@sparkandspiralsoftware",
        "https://www.youtube.com/@sparkandspiralsoftware",
        "https://discord.gg/Vh27jjmMBS",
        "https://sparkandspiral.ca"
      ]
    },
    {
      "@type": "WebSite",
      "@id": "https://logicpuzzles.ca/#website",
      "name": "Logic Puzzles CA",
      "alternateName": "LogicPuzzles.ca",
      "url": "https://logicpuzzles.ca",
      "description": "Play 30+ free logic puzzles and brain games online - Sudoku, Nonograms, Calcudoku, Mine Finder, and more. No signup, no downloads.",
      "inLanguage": "en",
      "publisher": { "@id": "https://logicpuzzles.ca/#organization" }
    }
  ]
};

// Site furniture (footer, cookie banner, app banner) stays off the /embed/*
// widgets, which render inside other people's pages.
function SiteOnly({ children }) {
  const { pathname } = useLocation();
  return pathname.startsWith('/embed/') ? null : children;
}

function App() {
  return (
    <HelmetProvider>
      <AccountProvider>
      <Helmet>
        <meta property="og:site_name" content="Logic Puzzles CA" />
        <meta property="og:locale" content="en_CA" />
        <script type="application/ld+json">
          {JSON.stringify(siteSchema)}
        </script>
      </Helmet>
      <CanonicalUrl />
      <TrailingSlashRedirect />
      <SiteOnly><SmartAppBanner /></SiteOnly>
      <main>
      <Suspense fallback={<div style={{ minHeight: '70vh' }} />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/logic-games" element={<LogicGames />} />
        <Route path="/word-games" element={<WordGames />} />
        <Route path="/card-games" element={<CardGames />} />
        <Route path="/apps" element={<Apps />} />
        <Route path="/apps/crowns" element={<CrownsAppPage />} />
        <Route path="/apps/binarysudokuplus" element={<BinarySudokuAppPage />} />
        <Route path="/apps/aquarium" element={<AquariumAppPage />} />
        <Route path="/apps/palettepath" element={<PalettePathAppPage />} />
        <Route path="/apps/dotstream" element={<DotStreamAppPage />} />
        <Route path="/apps/daily-logic-puzzles" element={<DailyLogicPuzzlesAppPage />} />
        <Route path="/press" element={<Press />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/account/delete" element={<DeleteAccountPage />} />
        <Route path="/plus" element={<PlusPage />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/spooktober" element={<Spooktober />} />
        <Route path="/crowns-puzzle" element={<CrownsPuzzlePage />} />
        <Route path="/embed" element={<EmbedInfoPage />} />
        <Route path="/embed/daily" element={<EmbedDailyPage />} />
        <Route path="/teachers-and-libraries" element={<TeachersLibraries />} />
        <Route path="/crowns-puzzle/how-to-solve" element={<CrownsPuzzleHowToPage />} />
        <Route path="/crowns-puzzle/hints" element={<CrownsPuzzleHintsPage />} />
        {/* Link-in-bio redirects (noindex, not in the sitemap) */}
        <Route path="/go/:platform" element={<GoPage />} />

        { /* Game content pages */ }
        <Route path="/games/crowns" element={<CrownsPage />} />
        <Route path="/games/binary-sudoku-plus" element={<BinarySudokuPlusPage />} />
        <Route path="/games/aquarium" element={<AquariumPage />} />
        <Route path="/games/dot-stream" element={<DotStreamPage />} />
        <Route path="/games/palette-path" element={<PalettePathPage />} />
        <Route path="/games/daily-logic-puzzles" element={<DailyLogicPuzzlesPage />} />
        <Route path="/games/solitaire-pro-collection" element={<SolitaireProCollectionPage />} />
        <Route path="/games/fox-hedge" element={<FoxHedgePage />} />
        <Route path="/games/nonograms" element={<NonogramsPage />} />
        <Route path="/games/calcudoku" element={<CalcudokuPage />} />

        { /* New game content pages (data-driven) */ }
        <Route path="/games/sudoku" element={<GameContentPage gameData={gameContentData.sudoku} />} />
        <Route path="/games/mine-finder" element={<GameContentPage gameData={gameContentData.minefinder} />} />
        <Route path="/games/tent-logic" element={<GameContentPage gameData={gameContentData.tents} />} />
        <Route path="/games/color-links" element={<GameContentPage gameData={gameContentData.colorlinks} />} />
        <Route path="/games/word-ladder" element={<GameContentPage gameData={gameContentData.wordladder} />} />
        <Route path="/games/nurikabe" element={<GameContentPage gameData={gameContentData.nurikabe} />} />
        <Route path="/games/fleet" element={<GameContentPage gameData={gameContentData.fleet} />} />
        <Route path="/games/light-up" element={<GameContentPage gameData={gameContentData.lightup} />} />
        <Route path="/games/cryptograms" element={<GameContentPage gameData={gameContentData.cryptograms} />} />
        <Route path="/games/futoshiki" element={<GameContentPage gameData={gameContentData.futoshiki} />} />
        <Route path="/games/galaxies" element={<GameContentPage gameData={gameContentData.galaxies} />} />
        <Route path="/games/kurodoku" element={<GameContentPage gameData={gameContentData.kurodoku} />} />
        <Route path="/games/logic-grid-puzzles" element={<GameContentPage gameData={gameContentData.logicgridpuzzles} />} />
        <Route path="/games/mosaic" element={<GameContentPage gameData={gameContentData.mosaic} />} />
        <Route path="/games/rectangles" element={<GameContentPage gameData={gameContentData.rectangles} />} />
        <Route path="/games/pipes" element={<GameContentPage gameData={gameContentData.pipes} />} />
        <Route path="/games/renzoku" element={<GameContentPage gameData={gameContentData.renzoku} />} />
        <Route path="/games/slant" element={<GameContentPage gameData={gameContentData.slant} />} />
        <Route path="/games/yin-yang" element={<GameContentPage gameData={gameContentData.yinyang} />} />
        <Route path="/games/skyscrapers" element={<GameContentPage gameData={gameContentData.skyscrapers} />} />
        <Route path="/games/thermometers" element={<GameContentPage gameData={gameContentData.thermometers} />} />
        <Route path="/games/dominosa" element={<GameContentPage gameData={gameContentData.dominosa} />} />
        <Route path="/games/norinori" element={<GameContentPage gameData={gameContentData.norinori} />} />
        <Route path="/games/lits" element={<GameContentPage gameData={gameContentData.lits} />} />
        <Route path="/games/word-wheel" element={<GameContentPage gameData={gameContentData.wordwheel} />} />
        <Route path="/games/slider-puzzle" element={<GameContentPage gameData={gameContentData.sliderpuzzle} />} />
        <Route path="/games/number-merge" element={<GameContentPage gameData={gameContentData.numbermerge} />} />
        <Route path="/games/block-merge" element={<GameContentPage gameData={gameContentData.blockmerge} />} />
        <Route path="/games/medieval-word-twister" element={<GameContentPage gameData={gameContentData.medievalwordtwister} />} />
        <Route path="/games/final-phrase" element={<GameContentPage gameData={gameContentData.finalphrase} />} />
        <Route path="/games/euchre" element={<GameContentPage gameData={gameContentData.euchre} />} />
        <Route path="/games/butterfly-tiles" element={<GameContentPage gameData={gameContentData.butterflymahjong} />} />

        { /* Printable puzzle pages */ }
        <Route path="/printable/crowns" element={<PrintableCrownsPage />} />
        <Route path="/printable/binary-sudoku-plus" element={<PrintableBinarySudokuPage />} />
        <Route path="/printable/aquarium" element={<PrintableAquariumPage />} />
        <Route path="/printable/sudoku" element={<PrintableSudokuPage />} />
        <Route path="/printable/tent-logic" element={<PrintableTentsPage />} />
        <Route path="/printable/nonograms" element={<PrintableNonogramsPage />} />
        <Route path="/printable/calcudoku" element={<PrintableCalcudokuPage />} />
        <Route path="/printable" element={<PrintableHubPage />} />
        <Route path="/printable/weekly" element={<WeeklyPackPage />} />
        {GENERIC_PRINTABLES.map(({ slug }) => (
          <Route key={slug} path={`/printable/${slug}`} element={<PrintablePuzzlePage slug={slug} />} />
        ))}
        {/* Ready-made packs (scripts/generate-printable-sets.mjs) */}
        <Route path="/printable/:type/:tier" element={<PrintableSetRoute />} />
        <Route path="/printable/:type/:tier/:pack" element={<PrintableSetRoute />} />
        <Route path="/fr/nonogrammes-a-imprimer" element={<PrintableSetRoute type="nonograms" pageKey="fr" />} />
        <Route path="/fr/puzzles-binaires-a-imprimer" element={<PrintableSetRoute type="binary-sudoku-plus" pageKey="fr" />} />

        { /* Legacy game paths - redirect to /play/ paths */ }
        <Route path="/crowns/*" element={<Navigate to="/play/crowns" replace />} />
        <Route path="/aquarium/*" element={<Navigate to="/play/aquarium" replace />} />
        <Route path="/binarysudokuplus/*" element={<Navigate to="/play/binarysudoku" replace />} />
        <Route path="/palettepath/*" element={<Navigate to="/play/palettepath" replace />} />
        <Route path="/dailylogicpuzzles/*" element={<Navigate to="/play/dailylogicpuzzles" replace />} />

        <Route path="/symmetrix/*" element={<Navigate to="/play/binarysudoku" replace />} />

        <Route path="/play/binarysudoku" element={<GamePage gamePath="binarysudokuplus" />} />
        <Route path="/play/palettepath" element={<GamePage gamePath="palettepath" />} />
        <Route path="/play/aquarium" element={<GamePage gamePath="aquarium" />} />
        <Route path="/play/crowns" element={<GamePage gamePath="crowns" />} />
        <Route path="/play/foxhedge" element={<GamePage gamePath="foxhedge" />} />

        <Route path="/dotstream/*" element={<Navigate to="/play/dotstream" replace />} />
        <Route path="/play/dotstream" element={<GamePage gamePath="dotstream" />} />

        {/* Added routes for new logic games */}
        <Route path="/play/dailylogicpuzzles" element={<GamePage gamePath="dailylogicpuzzles" />} />
        <Route path="/play/sudoku" element={<GamePage gamePath="sudoku" />} />
        <Route path="/play/sliderpuzzle" element={<GamePage gamePath="sliderpuzzle" />} />
        <Route path="/play/numbermerge" element={<GamePage gamePath="numbermerge" />} />
        <Route path="/play/blockmerge" element={<GamePage gamePath="blockmerge" />} />

        {/* Added routes for new word and card games */}
        <Route path="/play/medievalwordtwister" element={<GamePage gamePath="medievalwordtwister" />} />
        <Route path="/play/finalphrase" element={<GamePage gamePath="finalphrase" />} />
        <Route path="/play/solitaireprocollection" element={<GamePage gamePath="solitaireprocollection" />} />
        <Route path="/play/euchre" element={<GamePage gamePath="euchre" />} />
        <Route path="/euchre/*" element={<Navigate to="/play/euchre" replace />} />

        {/* Nonograms and Calcudoku Flutter games */}
        <Route path="/play/nonograms" element={<GamePage gamePath="nonograms" />} />
        <Route path="/nonograms/*" element={<Navigate to="/play/nonograms" replace />} />
        <Route path="/play/calcudoku" element={<GamePage gamePath="calcudoku" />} />
        <Route path="/calcudoku/*" element={<Navigate to="/play/calcudoku" replace />} />

        {/* Word Wheel */}
        <Route path="/play/wordwheel" element={<GamePage gamePath="wordwheel" />} />
        <Route path="/wordwheel/*" element={<Navigate to="/play/wordwheel" replace />} />

        {/* Mine Finder */}
        <Route path="/play/minefinder" element={<GamePage gamePath="minefinder" />} />
        <Route path="/minefinder/*" element={<Navigate to="/play/minefinder" replace />} />

        {/* Tent Logic */}
        <Route path="/play/tents" element={<GamePage gamePath="tents" />} />
        <Route path="/tents/*" element={<Navigate to="/play/tents" replace />} />

        {/* Color Links */}
        <Route path="/play/colorlinks" element={<GamePage gamePath="colorlinks" />} />
        <Route path="/colorlinks/*" element={<Navigate to="/play/colorlinks" replace />} />

        {/* Word Ladder */}
        <Route path="/play/wordladder" element={<GamePage gamePath="wordladder" />} />
        <Route path="/wordladder/*" element={<Navigate to="/play/wordladder" replace />} />


        {/* Nurikabe */}
        <Route path="/play/nurikabe" element={<GamePage gamePath="nurikabe" />} />
        <Route path="/nurikabe/*" element={<Navigate to="/play/nurikabe" replace />} />

        {/* Fleet */}
        <Route path="/play/fleet" element={<GamePage gamePath="fleet" />} />
        <Route path="/fleet/*" element={<Navigate to="/play/fleet" replace />} />

        {/* Light Up */}
        <Route path="/play/lightup" element={<GamePage gamePath="lightup" />} />
        <Route path="/lightup/*" element={<Navigate to="/play/lightup" replace />} />

        {/* Back-compat redirect (klondike was replaced by solitaireprocollection) */}
        <Route path="/play/klondike" element={<Navigate to="/play/solitaireprocollection" replace />} />


        {/* New logic puzzle games */}
        <Route path="/play/cryptograms" element={<GamePage gamePath="cryptograms" />} />
        <Route path="/cryptograms/*" element={<Navigate to="/play/cryptograms" replace />} />
        <Route path="/play/futoshiki" element={<GamePage gamePath="futoshiki" />} />
        <Route path="/futoshiki/*" element={<Navigate to="/play/futoshiki" replace />} />
        <Route path="/play/galaxies" element={<GamePage gamePath="galaxies" />} />
        <Route path="/galaxies/*" element={<Navigate to="/play/galaxies" replace />} />
        <Route path="/play/kurodoku" element={<GamePage gamePath="kurodoku" />} />
        <Route path="/kurodoku/*" element={<Navigate to="/play/kurodoku" replace />} />
        <Route path="/play/logicgridpuzzles" element={<GamePage gamePath="logicgridpuzzles" />} />
        <Route path="/logicgridpuzzles/*" element={<Navigate to="/play/logicgridpuzzles" replace />} />
        <Route path="/play/mosaic" element={<GamePage gamePath="mosaic" />} />
        <Route path="/mosaic/*" element={<Navigate to="/play/mosaic" replace />} />
        <Route path="/play/rectangles" element={<GamePage gamePath="rectangles" />} />
        <Route path="/rectangles/*" element={<Navigate to="/play/rectangles" replace />} />
        <Route path="/play/pipes" element={<GamePage gamePath="pipes" />} />
        <Route path="/pipes/*" element={<Navigate to="/play/pipes" replace />} />
        <Route path="/play/renzoku" element={<GamePage gamePath="renzoku" />} />
        <Route path="/renzoku/*" element={<Navigate to="/play/renzoku" replace />} />
        <Route path="/play/slant" element={<GamePage gamePath="slant" />} />
        <Route path="/slant/*" element={<Navigate to="/play/slant" replace />} />
        <Route path="/play/yinyang" element={<GamePage gamePath="yinyang" />} />
        <Route path="/yinyang/*" element={<Navigate to="/play/yinyang" replace />} />

        {/* New logic puzzle games - batch 2 */}
        <Route path="/play/skyscrapers" element={<GamePage gamePath="skyscrapers" />} />
        <Route path="/skyscrapers/*" element={<Navigate to="/play/skyscrapers" replace />} />
        <Route path="/play/thermometers" element={<GamePage gamePath="thermometers" />} />
        <Route path="/thermometers/*" element={<Navigate to="/play/thermometers" replace />} />
        <Route path="/play/dominosa" element={<GamePage gamePath="dominosa" />} />
        <Route path="/dominosa/*" element={<Navigate to="/play/dominosa" replace />} />
        <Route path="/play/norinori" element={<GamePage gamePath="norinori" />} />
        <Route path="/norinori/*" element={<Navigate to="/play/norinori" replace />} />
        <Route path="/play/lits" element={<GamePage gamePath="lits" />} />
        <Route path="/lits/*" element={<Navigate to="/play/lits" replace />} />

        {/* Butterfly Tiles */}
        <Route path="/play/butterflymahjong" element={<GamePage gamePath="butterflymahjong" />} />
        <Route path="/butterflymahjong/*" element={<Navigate to="/play/butterflymahjong" replace />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      </main>
      <SiteOnly>
        <Footer />
        <CookieConsent />
      </SiteOnly>
      </AccountProvider>
    </HelmetProvider>
  );
}

export default App;
